import { useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Bed, Bath, MapPin, Building2 } from 'lucide-react';
import { useStories } from '@/hooks/useStories';
import { StoryViewerDialog } from '@/components/landing/StoryViewerDialog';
import { useTranslation } from 'react-i18next';
import { pickLocalized } from '@/hooks/useLandingContent';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice } from '@/lib/formatPrice';
import { optimizedImageUrl } from '@/lib/imageOptimizer';

interface Props {
  /** Fallback properties used to build preview stories when real stories are unavailable. */
  fallbackProperties?: any[];
}

const PLACEHOLDER = 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop';

function propertiesToStories(props: any[]): any[] {
  return (props || []).slice(0, 12).map((p, idx) => {
    const imgs = (p.property_images || [])
      .slice()
      .sort((a: any, b: any) => {
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      });
    const slides = imgs.length
      ? imgs.slice(0, 8).map((i: any, j: number) => ({ url: i.url, position: j, caption: null }))
      : [{ url: PLACEHOLDER, position: 0, caption: null }];
    return {
      id: `preview-${p.id}`,
      title: p.title,
      cover_url: slides[0].url,
      link_url: `/property/${p.slug || p.id}`,
      display_order: idx,
      story_images: slides,
      property_id: p.id,
      price: p.price,
      currency: p.currency,
      listing_type: p.listing_type,
      property_type: p.property_type,
      city: p.city,
      sub_city: p.sub_city,
      bedrooms: p.bedrooms,
      bathrooms: p.bathrooms,
      area_sqm: p.area_sqm,
      area_unit: 'm²',
      description: p.description,
      reference_code: p.reference_code,
      slug: p.slug,
      contact_phone: p.contact_phone,
      contact_person: p.contact_person,
      agency_id: null,
      agency_name: null,
      agency_logo_url: null,
    };
  });
}

export function FeedStoriesRow({ fallbackProperties = [] }: Props) {
  const { data: realStories } = useStories();
  const { i18n } = useTranslation();
  const { displayCurrency, rate } = useCurrency();
  const lang = i18n.language || 'en';
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [startIdx, setStartIdx] = useState(0);

  const stories = useMemo(() => {
    if (realStories && realStories.length > 0) return realStories;
    return propertiesToStories(fallbackProperties);
  }, [realStories, fallbackProperties]);

  if (!stories || stories.length === 0) return null;

  const slidePage = (dir: 'left' | 'right') => {
    if (!ref.current) return;
    const w = ref.current.clientWidth;
    ref.current.scrollBy({ left: dir === 'left' ? -w : w, behavior: 'smooth' });
  };

  const openStory = (i: number) => {
    setStartIdx(i);
    setOpen(true);
  };

  return (
    <section className="my-2 relative">
      <div className="relative group/scroller px-6 md:px-12">
        <button
          onClick={() => slidePage('left')}
          aria-label="Previous"
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full border bg-background/95 shadow-md items-center justify-center hover:bg-background"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          onClick={() => slidePage('right')}
          aria-label="Next"
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full border bg-background/95 shadow-md items-center justify-center hover:bg-background"
        >
          <ChevronRight className="h-5 w-5" />
        </button>

        <div ref={ref} className="flex gap-3 overflow-x-auto scrollbar-hide snap-x scroll-smooth pb-1 px-1">

          {stories.map((s: any, i: number) => {
            const title = pickLocalized(s.title, lang);
            const img = optimizedImageUrl(s.cover_url || PLACEHOLDER, { width: 280, height: 380, quality: 75 });
            const lt = (s.listing_type || '').toLowerCase();
            const dealLabel = lt === 'rent' || lt === 'for_rent' ? 'For Rent' : lt === 'sale' || lt === 'for_sale' || lt === 'buy' ? 'For Sale' : null;
            const dealClass = dealLabel === 'For Rent'
              ? 'bg-amber-500 text-white'
              : 'bg-primary text-primary-foreground';
            return (
              <button
                key={s.id}
                onClick={() => openStory(i)}
                className="snap-start shrink-0 basis-[42%] sm:basis-[26%] md:basis-[19%] lg:basis-[15%] text-left group"
              >
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden border-2 border-primary/40 bg-muted">
                  <img src={img} alt={title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />

                  {/* Top row: deal badge + agency logo */}
                  <div className="absolute top-2 left-2 right-2 flex items-start justify-between gap-2">
                    {dealLabel && (
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-md shadow ${dealClass}`}>
                        {dealLabel}
                      </span>
                    )}
                    <div className="ml-auto h-11 w-11 rounded-full bg-background border-2 border-background shadow-lg flex items-center justify-center overflow-hidden ring-1 ring-border">
                      {s.agency_logo_url
                        ? <img src={s.agency_logo_url} alt={s.agency_name || 'Agency'} className="w-full h-full object-cover" loading="lazy" />
                        : <Building2 className="h-5 w-5 text-muted-foreground" />}
                    </div>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
                    <div className="font-bold text-xs drop-shadow truncate">
                      {formatPrice(s.price ?? 0, s.currency || 'ETB', displayCurrency, rate)}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] opacity-90 mt-0.5">
                      {s.bedrooms != null && <span className="flex items-center gap-0.5"><Bed className="h-2.5 w-2.5" />{s.bedrooms}</span>}
                      {s.bathrooms != null && <span className="flex items-center gap-0.5"><Bath className="h-2.5 w-2.5" />{s.bathrooms}</span>}
                      {s.area_sqm != null && <span className="truncate">{Math.round(s.area_sqm)}m²</span>}
                    </div>
                    {(s.sub_city || s.city) && (
                      <div className="flex items-center gap-1 text-[10px] opacity-90 truncate mt-0.5">
                        <MapPin className="h-2.5 w-2.5 shrink-0" />
                        <span className="truncate">{[s.sub_city, s.city].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>


      <StoryViewerDialog stories={stories as any} startIndex={startIdx} open={open} onOpenChange={setOpen} />
    </section>
  );
}
